/**
 * §4.1's pay-now flow, as one dialog.
 *
 * Three decisions in here are worth stating, because each one is a rule from
 * the specification rather than a design preference.
 *
 * **The idempotency key is generated when the dialog opens, not when the
 * request is sent.** §6 requires the header, and the protection it buys is that
 * a merchant tapping Pay twice on a slow connection gets one checkout. A key
 * made per request would make every retry a second payment — the exact failure
 * the header exists to prevent. So it is minted once per intention and reused
 * for every attempt at that intention.
 *
 * **The amount is the merchant's choice, not the client's calculation.** The
 * server holds the authoritative balance (§4.1 step 2); this dialog offers the
 * balance as a default and lets it be changed, and the server decides whether
 * what came back is payable.
 *
 * **Nothing here touches a card number.** The provider's hosted checkout does
 * that, and this screen's last act is to send the merchant there — §8 forbids
 * SmartSpaza from ever holding one.
 */

import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { ApiError } from "@/api/client";
import { Button, Dialog, SelectField, TextField } from "@/components/ui";
import { formatMoney } from "@/lib/format";
import {
  premiumBillingRepository,
  type PaymentChannel,
  type PremiumOverview,
} from "@/repositories/premiumBillingRepository";

const CHANNEL_LABELS: Record<PaymentChannel, string> = {
  CARD: "Card",
  EFT: "Instant EFT / PayShap",
  DEBIT_ORDER: "Debit order",
};

export interface PayNowDialogProps {
  open: boolean;
  onClose: () => void;
  overview: PremiumOverview;
  onPaid: () => void;
}

export function PayNowDialog({
  open,
  onClose,
  overview,
  onPaid,
}: PayNowDialogProps) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState<PaymentChannel>(
    overview.payment_methods[0] ?? "CARD",
  );
  const [key, setKey] = useState("");

  // A fresh key per opening of the dialog, and the same key for every attempt
  // while it stays open. Closing and reopening is a new intention and gets a new
  // key; retrying the same attempt does not.
  useEffect(() => {
    if (open) {
      setKey(crypto.randomUUID());
      setAmount(
        centsToInput(overview.amount_due_cents || overview.daily_rate_cents),
      );
    }
  }, [open, overview.amount_due_cents, overview.daily_rate_cents]);

  const start = useMutation({
    mutationFn: () =>
      premiumBillingRepository.startPayment(
        { amount_cents: inputToCents(amount), method },
        key,
      ),
    onSuccess: (intent) => {
      if (intent.checkout_url) {
        // The provider's own page. The merchant comes back to a dashboard that
        // says PAYMENT_PENDING until the webhook lands — §BR-07 means nothing is
        // paid until the provider says so, and this screen must not pretend.
        window.location.assign(intent.checkout_url);
        return;
      }
      onPaid();
    },
  });

  const error =
    start.error instanceof ApiError
      ? start.error.detail
      : start.error
        ? "Something went wrong."
        : "";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Pay your SmartSpaza account"
      description={
        overview.amount_due_cents > 0
          ? `${formatMoney(overview.amount_due)} is outstanding. You can pay all of it, part of it, or more to cover the days ahead.`
          : `Nothing is owing. Anything you pay now covers the days ahead at ${formatMoney(overview.account.daily_rate)} a day.`
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={start.isPending}>
            Cancel
          </Button>
          <Button
            variant="money"
            loading={start.isPending}
            disabled={!isPayable(amount)}
            onClick={() => start.mutate()}
          >
            Continue to payment
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <TextField
          label="Amount"
          inputMode="decimal"
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
          hint="In rand. You can change this."
        />

        <SelectField
          label="Pay with"
          value={method}
          onChange={(event) => setMethod(event.target.value as PaymentChannel)}
          options={overview.payment_methods.map((channel) => ({
            value: channel,
            label: CHANNEL_LABELS[channel],
          }))}
        />

        {error && (
          <p role="alert" className="text-sm text-danger-700">
            {error}
          </p>
        )}

        <p className="text-xs text-ink-500">
          You will finish the payment on your bank or card provider&rsquo;s own
          page. SmartSpaza never sees or stores your card details.
        </p>
      </div>
    </Dialog>
  );
}

/**
 * Rand in the box, cents on the wire.
 *
 * The conversion happens here, once, at the edge — and it goes through the
 * string rather than through arithmetic on a float, because `29.99 * 100` is
 * `2998.9999999999995` and `Math.round` on it is a fix that hides how it got
 * there. Splitting on the point and padding is exact for every input a person
 * can type.
 */
function inputToCents(value: string): number {
  const cleaned = value.replace(/[^\d.]/g, "");
  const [whole = "0", fraction = ""] = cleaned.split(".");
  const cents = (fraction + "00").slice(0, 2);
  return Number(whole || "0") * 100 + Number(cents);
}

function centsToInput(cents: number): string {
  return (cents / 100).toFixed(2);
}

function isPayable(value: string): boolean {
  return inputToCents(value) > 0;
}
